import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Check, Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { popupAPI } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const STORAGE_KEY = 'reshapersPopup';

export const MarketingPopup = () => {
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get storage data
  const getStorageData = useCallback(() => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }, []);

  // Set storage data
  const setStorageData = useCallback((data) => {
    try {
      const existing = getStorageData();
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...data }));
    } catch {
      // Ignore storage errors
    }
  }, [getStorageData]);

  // Check if popup should be shown based on frequency
  const shouldShowBasedOnFrequency = useCallback((frequency) => {
    const storage = getStorageData();
    
    // Check permanent dismissal
    if (storage.dismissed === true) {
      return false;
    }

    const now = Date.now();
    
    switch (frequency) {
      case 'always':
        return true;
      case 'once_per_session':
        return !storage.shownThisSession;
      case 'once_per_day':
        const lastShown = storage.lastShown;
        if (!lastShown) return true;
        const oneDayMs = 24 * 60 * 60 * 1000;
        return (now - lastShown) > oneDayMs;
      default:
        return true;
    }
  }, [getStorageData]);

  // Check if current page matches display pages
  const shouldShowOnPage = useCallback((displayPages) => {
    if (!displayPages || displayPages.includes('all')) {
      return true;
    }
    
    const currentPath = location.pathname;
    return displayPages.some(page => {
      // Exact match
      if (page === currentPath) return true;
      // Starts with match for nested routes
      if (page.endsWith('*') && currentPath.startsWith(page.slice(0, -1))) return true;
      return false;
    });
  }, [location.pathname]);

  // Fetch popup configuration
  useEffect(() => {
    const fetchPopup = async () => {
      try {
        const res = await popupAPI.getActive();
        if (res.data.active && res.data.popup) {
          setPopup(res.data.popup);
        }
      } catch (error) {
        console.error('Failed to fetch popup:', error);
      }
    };

    fetchPopup();
  }, []);

  // Show popup with delay
  useEffect(() => {
    if (!popup) return;

    // Check page targeting
    if (!shouldShowOnPage(popup.display_pages)) {
      return;
    }

    // Check frequency
    if (!shouldShowBasedOnFrequency(popup.show_frequency)) {
      return;
    }

    // Show with delay
    const delay = (popup.show_delay_seconds || 0) * 1000;
    const timer = setTimeout(() => {
      setIsVisible(true);
      // Mark as shown
      setStorageData({
        shownThisSession: true,
        lastShown: Date.now()
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [popup, shouldShowOnPage, shouldShowBasedOnFrequency, setStorageData]);

  // Handle close
  const handleClose = () => {
    if (dontShowAgain) {
      setStorageData({ dismissed: true });
    }
    setIsVisible(false);
  };

  // Handle CTA click
  const handleCtaClick = () => {
    if (popup?.cta_link) {
      if (popup.cta_link.startsWith('http')) {
        window.open(popup.cta_link, '_blank');
      } else {
        navigate(popup.cta_link);
      }
    }
    handleClose();
  };

  // Handle newsletter signup
  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await popupAPI.subscribeNewsletter(email);
      setSubscribed(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error('Newsletter signup failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!popup || !isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={handleClose}
            data-testid="popup-backdrop"
          />

          {/* Popup Container - Flexbox centering */}
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-[90vw] max-w-md pointer-events-auto"
              data-testid="marketing-popup"
            >
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl"
              style={{ backgroundColor: popup.background_color }}
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-black/10 hover:bg-black/20 transition-colors z-10"
                style={{ color: popup.text_color }}
                data-testid="popup-close-btn"
              >
                <X size={18} />
              </button>

              {/* Image */}
              {popup.image_url && (
                <div className="w-full aspect-video">
                  <img
                    src={popup.image_url}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-6 sm:p-8" style={{ color: popup.text_color }}>
                {popup.title && (
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-2" data-testid="popup-title">
                    {popup.title}
                  </h2>
                )}
                
                {popup.subtitle && (
                  <p className="font-medium opacity-80 mb-2" data-testid="popup-subtitle">
                    {popup.subtitle}
                  </p>
                )}
                
                {popup.description && (
                  <p className="text-sm opacity-70 mb-6" data-testid="popup-description">
                    {popup.description}
                  </p>
                )}

                {/* Newsletter form */}
                {popup.enable_newsletter && !subscribed && (
                  <form onSubmit={handleNewsletterSignup} className="mb-4">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" size={18} />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email"
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-500 text-slate-800"
                          required
                          data-testid="popup-email-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-5 py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors disabled:opacity-50"
                        data-testid="popup-subscribe-btn"
                      >
                        {isSubmitting ? (
                          <Loader2 className="animate-spin" size={20} />
                        ) : (
                          'Subscribe'
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Success message */}
                {subscribed && (
                  <div className="flex items-center gap-2 p-4 bg-emerald-50 text-emerald-700 rounded-xl mb-4">
                    <Check size={20} />
                    <span className="font-medium">Successfully subscribed!</span>
                  </div>
                )}

                {/* CTA Button */}
                {!popup.enable_newsletter && (
                  <button
                    onClick={handleCtaClick}
                    className={`w-full py-3.5 rounded-xl font-semibold text-base transition-all ${
                      popup.cta_style === 'primary'
                        ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:shadow-lg hover:shadow-violet-500/25'
                        : popup.cta_style === 'secondary'
                        ? 'bg-slate-800 text-white hover:bg-slate-900'
                        : 'border-2 border-current hover:bg-black/5'
                    }`}
                    data-testid="popup-cta-btn"
                  >
                    {popup.cta_text}
                  </button>
                )}

                {popup.enable_newsletter && !subscribed && (
                  <button
                    onClick={handleCtaClick}
                    className="w-full py-2 text-sm font-medium opacity-70 hover:opacity-100 transition-opacity"
                    data-testid="popup-cta-link"
                  >
                    {popup.cta_text} →
                  </button>
                )}

                {/* Don't show again checkbox */}
                {popup.show_dont_show_again && (
                  <label className="flex items-center gap-2 mt-5 text-xs opacity-60 cursor-pointer hover:opacity-80 transition-opacity">
                    <input
                      type="checkbox"
                      checked={dontShowAgain}
                      onChange={(e) => setDontShowAgain(e.target.checked)}
                      className="rounded border-current"
                      data-testid="popup-dont-show-checkbox"
                    />
                    Don't show this again
                  </label>
                )}
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MarketingPopup;
